import React, { useState } from 'react';
import { Card, Button, Container, Row, Col, Modal } from 'react-bootstrap';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import styled from 'styled-components';

// ✅ Styled Components
const StyledContainer = styled(Container)`
  background-color: #232830;
  color: #FFFFFF;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const StyledHeading = styled.h2`
  font-size: 22px;
  font-weight: bold;
  color: #FF7C24;
  text-align: center;
  margin-bottom: 20px;
`;

const QuestionText = styled.h3`
  font-size: 20px;
  font-weight: bold;
  color: #FFFFFF;
  text-align: center;
  margin-bottom: 20px;
`;

const StyledCard = styled(Card)`
  background-color: ${(props) => (props.selected ? (props.isCorrect ? '#2ECC71' : '#FF4C4C') : '#FFFFFF')};
  color: ${(props) => (props.selected ? '#FFFFFF' : '#232830')};
  border-radius: 15px;
  padding: 15px;
  margin-bottom: 10px;
  text-align: center;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  transition: transform 0.2s ease-in-out;
  &:hover {
    transform: scale(1.02);
  }
`;

const StandardButton = styled(Button)`
  background-color: ${(props) => (props.disabled ? '#555' : '#FF7C24')};
  border-color: ${(props) => (props.disabled ? '#555' : '#FF7C24')};
  color: #FFFFFF;
  font-size: 18px;
  font-weight: bold;
  border-radius: 12px;
  padding: 12px;
  width: 80%;
  max-width: 300px;
  margin-top: 20px;
  transition: background-color 0.2s ease-in-out;
  &:hover {
    background-color: ${(props) => (props.disabled ? '#555' : '#E66A1A')};
    border-color: ${(props) => (props.disabled ? '#555' : '#E66A1A')};
  }
`;

const TrueFalsePSQuestion = ({ question, onNext, maxWrongAnswersRemaining, onUpgrade }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [isCorrect, setIsCorrect] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [wrongAttempts, setWrongAttempts] = useState(0);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showHint, setShowHint] = useState(false);

  const db = getFirestore();
  const auth = getAuth();

  const handleOptionSelect = async (option) => {
    if (selectedOption === null) {
      setSelectedOption(option);

      // ✅ Determine if the answer is correct
      const correct = option === question.correctanswer;
      setIsCorrect(correct);
      setShowFeedback(true);

      if (!correct) {
        setWrongAttempts((prev) => prev + 1);
        if (wrongAttempts + 1 > maxWrongAnswersRemaining) {
          setShowPaywall(true);
        }
      }

      // ✅ Store answer in Firestore
      try {
        const currentUser = auth.currentUser;
        await addDoc(collection(db, 'userpracticesessionanswers'), {
          date: serverTimestamp(),
          lessonid: question.lessonid,
          questionid: question.questionid,
          submittedanswer: option,
          userid: currentUser ? currentUser.uid : 'anonymous',
          iscorrect: correct,
        });
      } catch (error) {
        console.error('Error logging answer:', error);
      }
    }
  };

  const handleContinue = () => {
    onNext(isCorrect);
    setShowFeedback(false);
    setSelectedOption(null);
    setIsCorrect(false);
  };

  return (
    <StyledContainer>
      <StyledHeading>True or False</StyledHeading>
      <QuestionText>{question.questionname}</QuestionText>

      <Row className="g-3">
        {["✅ True", "❌ False"].map((option, index) => (
          <Col xs={12} key={index}>
            <StyledCard
              onClick={() => handleOptionSelect(option)}
              selected={selectedOption === option}
              isCorrect={isCorrect}
            >
              {option}
            </StyledCard>
          </Col>
        ))}
      </Row>

      {showFeedback && (
        <p style={{ fontSize: '18px', fontWeight: 'bold', color: isCorrect ? '#2ECC71' : '#FF4C4C' }}>
          {isCorrect ? "✅ Correct! Click Continue." : "❌ Incorrect. Click Continue."}
        </p>
      )}

      {showFeedback && (
        <StandardButton onClick={handleContinue}>Continue</StandardButton>
      )}

      <Button
        onClick={() => setShowHint(true)}
        style={{
          backgroundColor: '#007bff',
          marginTop: '10px',
          fontSize: '16px',
          fontWeight: 'bold',
        }}
      >
        Need a Hint?
      </Button>

      {showHint && (
        <p style={{ fontSize: '16px', color: '#ffffff', textAlign: 'center', marginTop: '15px' }}>
          {question.helpText || 'No hint available.'}
        </p>
      )}

      {/* Paywall Modal */}
      <Modal show={showPaywall} onHide={() => setShowPaywall(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Unlock More Practice</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Upgrade to FinTorro Pro and get:</p>
          <ul>
            <li>Unlimited Lessons</li>
            <li>Unlimited Practice Sessions</li>
            <li>Unlimited Mistakes</li>
            <li>Advanced AI Learning</li>
            <li>Full Course Access</li>
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={onUpgrade}>
            Upgrade Now
          </Button>
        </Modal.Footer>
      </Modal>
    </StyledContainer>
  );
};

export default TrueFalsePSQuestion;
